import { apiClient } from '../client'
import type { ComponentAccessResponse } from '../../app/modules/types/componentAccess'
import { OWNS_COMPONENT_REFUSAL } from '../ownsErrors'

/**
 * Component catalog (orch-side equivalent of Django's deprecated `NodeType`).
 *
 * Each Component describes one kind of work a node in a workflow graph can
 * do — its name, schema, ports, descriptions, and runtime health
 * (heartbeat-driven `is_active`). Replaces the FE's previous calls to
 * Django's `/workflow/node-types/*` once `apps/workflow` is removed.
 */
export const componentsApi = {
  /**
   * Which components the current user may use (plan + role). Display only — the
   * backend refuses locked components itself. 503 means the catalog or the
   * permission service is down: callers must treat access as unknown, not allowed.
   */
  getAccess: async (organizationId: string): Promise<ComponentAccessResponse> => {
    const { data } = await apiClient.get(
      `/organizations/${organizationId}/permissions/components/access/`,
      OWNS_COMPONENT_REFUSAL,
    )
    return data
  },

  list: async (): Promise<any> => {
    const { data } = await apiClient.get('/workflow/components/')
    return data
  },

  getById: async (componentId: string): Promise<any> => {
    const { data } = await apiClient.get(`/workflow/components/${componentId}/`)
    return data
  },

  /** Default config object derived from the component's JSON schema. */
  getDefaultConfig: async (componentId: string): Promise<any> => {
    const { data } = await apiClient.get(
      `/workflow/components/${componentId}/default-config/`
    )
    return data
  },

  /** Frontend-shaped schema (orch reformats raw JSON Schema for the UI). */
  getFrontendSchema: async (componentId: string): Promise<any> => {
    const { data } = await apiClient.get(
      `/workflow/components/${componentId}/frontend-schema/`
    )
    return data
  },

  /** Validate a config object against the component's schema. */
  validateConfig: async (
    componentId: string,
    config: Record<string, unknown>
  ): Promise<{ valid: boolean; errors: { path: string; message: string }[] }> => {
    const { data } = await apiClient.post(
      `/workflow/components/${componentId}/validate-config/`,
      { config }
    )
    return data
  },
}
